import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Send as SendIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import RichTextDisplay from '../../components/common/RichTextDisplay';
import { 
  getProblemById, 
  getProblemsBySession,
  submitAnswer, 
  getUserProblemSubmission, 
  Problem, 
  ProblemSubmission 
} from '../../services/problemService';
import {
  getSessionById,
  getProblemGroupById,
  Session,
  ProblemGroup
} from '../../services/sessionService';
import type { StudentAchievement } from '../../types/achievement';
import AchievementDialog from '../../components/achievements/AchievementDialog';

// Helper function to convert any image path to an S3 URL
function getImageS3Url(imagePath: string): string {
  // Case 1: Already a full URL (S3 URL or external image URL)
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Get just the filename without any path
  const filename = imagePath.split('/').pop();
  if (!filename) {
    console.error('Invalid image path:', imagePath);
    return '';
  }
  
  // Use S3 bucket name from environment variable if available
  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'mathwiz-images';
  const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  
  // Return the S3 URL in the appropriate format
  return `https://${bucketName}.s3.${region}.amazonaws.com/uploads/${filename}`;
}

function ProblemDetailPage() {
  const { sessionId, problemId } = useParams<{ sessionId: string; problemId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [submission, setSubmission] = useState<ProblemSubmission | null>(null);
  const [answer, setAnswer] = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [/* successMessage */, setSuccessMessage] = useState<string | null>(null);
  const [hasNextProblem, setHasNextProblem] = useState(false);
  const [hasPrevProblem, setHasPrevProblem] = useState(false);
  const [nextProblemId, setNextProblemId] = useState<number | null>(null);
  const [prevProblemId, setPrevProblemId] = useState<number | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [problemGroup, setProblemGroup] = useState<ProblemGroup | null>(null);
  
  // Achievement state
  const [newAchievements, setNewAchievements] = useState<StudentAchievement[]>([]);
  const [showAchievementDialog, setShowAchievementDialog] = useState(false);
  const [problemIndex, setProblemIndex] = useState<number>(0);
  
  // Helper function to check if the problem group has ended
  const isGroupEnded = (): boolean => {
    if (!problemGroup || !problemGroup.endDate) {
      return false;
    }

    const now = new Date();
    const endDate = new Date(problemGroup.endDate);
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    return nowDateOnly > endDateOnly;
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Reset all state values when problem changes
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        setSubmission(null);
        setAnswer('');
        setSelectedOptionId(null);
        
        if (!problemId || !sessionId) {
          setError('Invalid problem or session ID');
          setLoading(false);
          return;
        }
        
        const problemIdNum = parseInt(problemId);
        const sessionIdNum = parseInt(sessionId);
        
        // Fetch the current problem
        const fetchedProblem = await getProblemById(problemIdNum);
        
        if (!fetchedProblem) {
          setError('Problem not found');
          setLoading(false);
          return;
        }
        
        setProblem(fetchedProblem);
        
        // Fetch session, problem group, and other data in parallel
        const [
          fetchedSession, 
          fetchedProblemGroup,
          allSessionProblems,
          existingSubmission
        ] = await Promise.all([
          getSessionById(sessionIdNum),
          getProblemGroupById(fetchedProblem.groupId),
          getProblemsBySession(sessionIdNum),
          getUserProblemSubmission(problemIdNum)
        ]);
        
        setSession(fetchedSession);
        setProblemGroup(fetchedProblemGroup);
        
        // Filter problems to only include those in the same group
        const problemsInSameGroup = allSessionProblems.filter(p => p.groupId === fetchedProblem.groupId);
        
        // Sort problems by ID to determine next and previous within the same group
        const sortedProblems = [...problemsInSameGroup].sort((a, b) => a.id - b.id);

        // Find current problem's index - ensure we're comparing numbers to numbers
        const currentIndex = sortedProblems.findIndex(p => Number(p.id) === Number(problemIdNum));

        // Fix the problem index if it's not found
        let actualIndex = currentIndex;

        // If the current problem is not found in the sorted list,
        // find the closest problem or default to the first problem
        if (actualIndex === -1 && sortedProblems.length > 0) {
          // Find the closest problem by ID (first one greater than the current ID)
          const closestIndex = sortedProblems.findIndex(p => Number(p.id) >= Number(problemIdNum));

          if (closestIndex !== -1) {
            actualIndex = closestIndex;
          } else {
            // Default to the first problem if none is found
            actualIndex = 0;
          }
        }

        // Check if there are previous and next problems in the same group
        const hasPrev = actualIndex > 0;
        const hasNext = actualIndex < sortedProblems.length - 1;

        // Set problem index for display
        setProblemIndex(actualIndex);

        setHasPrevProblem(hasPrev);
        setHasNextProblem(hasNext);

        // Store the IDs of the next and previous problems if they exist
        if (hasPrev) {
          setPrevProblemId(sortedProblems[actualIndex - 1].id);
        }

        if (hasNext) {
          setNextProblemId(sortedProblems[actualIndex + 1].id);
        }
        
        // Set submission data if it exists
        // Backend returns an array of submissions, we need to get the most recent one
        if (existingSubmission) {
          console.log('[ProblemDetailPage] Received submission data:', existingSubmission);

          // Handle both array and single object responses
          const submissionData = Array.isArray(existingSubmission)
            ? existingSubmission[0]  // Take the first (most recent) submission
            : existingSubmission;

          if (submissionData) {
            console.log('[ProblemDetailPage] Setting submission:', submissionData);
            setSubmission(submissionData);

            // Only populate answer field for open-ended problems
            // For multiple choice, submittedAnswer is empty string in DB, so don't use it
            if (fetchedProblem.problemType !== 'multiple_choice' && submissionData.submittedAnswer) {
              setAnswer(submissionData.submittedAnswer);
            }
          }
        } else {
          console.log('[ProblemDetailPage] No existing submission found');
        }
      } catch (err) {
        console.error('Error fetching problem:', err);
        setError('Failed to load problem. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [problemId, sessionId]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      setNewAchievements([]);
      
      if (!problemId) {
        setError('Problem ID is missing');
        return;
      }
      
      let result;
      
      if (problem?.problemType === 'multiple_choice') {
        if (selectedOptionId === null) {
          setError('Please select an option');
          setSubmitting(false);
          return;
        }
        
        // Log the data we're about to send
        console.log('Submitting multiple choice answer:', {
          problemId: parseInt(problemId),
          selectedOptionId: selectedOptionId
        });
        
        // Submit selected option for multiple choice
        // Pass empty string for submittedAnswer to match backend expectations
        result = await submitAnswer(parseInt(problemId), '', selectedOptionId);
      } else {
        // Only validate answer for open-ended problems
        if (!answer || !answer.trim()) {
          setError('Please enter an answer');
          setSubmitting(false);
          return;
        }

        // Submit text answer for open-ended problem
        result = await submitAnswer(parseInt(problemId), answer.trim());
      }
      
      // Log the response for debugging
      console.log('Problem submission result:', result);
      console.log('Achievement earned?', result.achievementEarned);
      console.log('New achievements:', result.newAchievements);
      
      // Update submission state with result
      setSubmission(result.submission);
      
      // Show success message
      setSuccessMessage(result.message);
      
      // Check if any achievements were earned
      if (result.achievementEarned && result.newAchievements && result.newAchievements.length > 0) {
        console.log('Showing achievement dialog with:', result.newAchievements);
        setNewAchievements(result.newAchievements);
        setShowAchievementDialog(true);
      }

      // Clear any previous errors
      setError(null);
    } catch (err) {
      const error = err as Error;
      console.error('Error submitting answer:', error);
      setError(error.message || 'Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Function to restart problem attempt
  // const handleTryAgain = () => {
  //   // Clear success message and reset form for a new attempt
  //   setSuccessMessage(null);
  //   setAnswer('');
  // };
  
  const goBack = () => {
    navigate(`/sessions/${sessionId}/problems`);
  };
  
  // Function to render result message
  const renderResultMessage = () => {
    // Only show either the achievement dialog or the regular result message, not both
    if (!submission || (showAchievementDialog && submission.isCorrect)) return null;
    
    if (submission.isCorrect) {
      return (
        <Alert 
          severity="success" 
          icon={<CheckIcon fontSize="inherit" />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>Correct Answer!</AlertTitle>
          Great job! You've successfully solved this problem.
          {submission.attemptCount > 1 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              It took you {submission.attemptCount} attempts to solve this problem.
            </Typography>
          )}
        </Alert>
      );
    } else {
      return (
        <Alert 
          severity="error" 
          icon={<CloseIcon fontSize="inherit" />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>Incorrect Answer</AlertTitle>
          Your answer was not correct. You can modify your answer and try again.
          {submission.attemptCount > 1 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              This is attempt #{submission.attemptCount}.
            </Typography>
          )}
        </Alert>
      );
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ pt: 3, pb: 6 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={goBack}
              sx={{ mb: 1 }}
            >
              Back to Problems
            </Button>
            {session && problemGroup && (
              <Typography variant="subtitle1" color="text.secondary">
                {session.title} • {problemGroup.title}
              </Typography>
            )}
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : problem ? (
            <>
              <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2 
                    }}
                  >
                    <Typography variant="h5" component="h1" fontWeight="medium">
                      Problem {problemIndex + 1}
                    </Typography>
                    <Chip 
                      label={`Difficulty: ${problem.difficultyLevel}`}
                      color={
                        problem.difficultyLevel <= 2 ? 'success' :
                        problem.difficultyLevel <= 4 ? 'primary' : 'error'
                      }
                      size="small"
                    />
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <RichTextDisplay 
                    content={problem.problemStatement}
                    variant="body1" 
                    sx={{ mb: 3 }}
                  />
                  
                  {problem.imagePath && (
                    <Box sx={{ my: 3, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Problem Diagram:
                      </Typography>
                      <Box 
                        sx={{ 
                          border: '1px solid #eee', 
                          borderRadius: '4px', 
                          p: 2,
                          bgcolor: 'background.paper',
                          display: 'inline-block'
                        }}
                      >
                        <img 
                          src={getImageS3Url(problem.imagePath)}
                          alt="Problem diagram" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '300px',
                            display: 'block'
                          }} 
                          onError={(e) => {
                            console.error('Image failed to load:', problem.imagePath);
                            const target = e.target as HTMLImageElement;
                            target.onerror = null; // Prevent infinite error loops
                            target.style.display = 'none';
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Answer submission section */}
                  <Box sx={{ mt: 4 }}>
                    {/* Removed the blue info alert that was duplicating the success message */}
                    
                    {renderResultMessage()}
                    
                    {/* Check if group has ended */}
                    {isGroupEnded() ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <AlertTitle>Submission Period Ended</AlertTitle>
                        The submission period for this problem group has ended. You can still view the problem and your previous submissions, but cannot submit new answers.
                      </Alert>
                    ) : (!submission || !submission.isCorrect) && (
                      <form onSubmit={handleSubmit}>
                        <Typography variant="subtitle1" gutterBottom>
                          Your Answer:
                        </Typography>
                        
                        {problem.problemType === 'multiple_choice' ? (
                          <Box sx={{ mb: 2 }}>
                            <FormControl component="fieldset">
                              <RadioGroup
                                value={selectedOptionId || ''}
                                onChange={(e) => setSelectedOptionId(parseInt(e.target.value, 10))}
                              >
                                {problem.options?.map(option => (
                                  <FormControlLabel
                                    key={option.id}
                                    value={option.id}
                                    control={<Radio />}
                                    label={option.optionText}
                                    disabled={submitting}
                                  />
                                ))}
                              </RadioGroup>
                            </FormControl>
                          </Box>
                        ) : (
                          <TextField
                            fullWidth
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            placeholder="Enter your answer here..."
                            variant="outlined"
                            disabled={submitting}
                            sx={{ mb: 2 }}
                          />
                        )}
                        
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={submitting || (problem.problemType === 'open_ended' ? !answer.trim() : selectedOptionId === null)}
                          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                        >
                          {submitting ? 'Submitting...' : 'Submit Answer'}
                        </Button>
                      </form>
                    )}
                  </Box>
                </CardContent>
              </Card>
              
              {/* Navigation buttons */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* Previous Problem button */}
                {hasPrevProblem && prevProblemId && (
                  <Grid item xs={hasNextProblem ? 6 : 12}>
                    <Button
                      startIcon={<PrevIcon />}
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate(`/sessions/${sessionId}/problems/${prevProblemId}`)}
                    >
                      Previous Problem
                    </Button>
                  </Grid>
                )}
                
                {/* Only show Next Problem OR Back to Problems, never both */}
                {hasNextProblem && nextProblemId ? (
                  <Grid item xs={hasPrevProblem ? 6 : 12}>
                    <Button
                      endIcon={<NextIcon />}
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(`/sessions/${sessionId}/problems/${nextProblemId}`)}
                    >
                      Next Problem
                    </Button>
                  </Grid>
                ) : (
                  <Grid item xs={hasPrevProblem ? 6 : 12}>
                    <Button
                      startIcon={<ArrowBackIcon />}
                      fullWidth
                      variant={hasPrevProblem ? "outlined" : "contained"}
                      onClick={() => navigate(`/sessions/${sessionId}/problems`)}
                    >
                      Back to Problems
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>
          ) : (
            <Alert severity="warning" sx={{ my: 2 }}>
              Problem not found
            </Alert>
          )}
        </Box>
      </Container>
      
      {/* Achievement Earned Dialog - Using the reusable AchievementDialog component */}
      <AchievementDialog
        open={showAchievementDialog}
        onClose={() => setShowAchievementDialog(false)}
        newAchievements={newAchievements}
        onViewTrophyCase={() => navigate('/student/achievements')}
      />
    </MainLayout>
  );
}

export default ProblemDetailPage;